<template>
  <div class="chart-card">
    <div class="chart-header">
      <div class="chart-title">Cumulative Projects Over Time in BossDB</div>
      <div class="chart-subtitle">
        {{ yearRangeLabel }} • {{ totalAllYears }} total
      </div>
    </div>

    <div class="chart-body">
      <div v-if="loading" class="chart-empty">Loading…</div>

      <div v-else class="bars">
        <div
          v-for="(b, idx) in bars"
          :key="idx"
          class="bar-col"

        >
        <div class="bar-slot">
          <div class="bar-value-top">
            <span v-if="b.value > 0">{{ b.value }}</span>
            <span v-else>&nbsp;</span>
          </div>

          <div class="bar" :style="{ height: b.heightPx + 'px' }"></div>
        </div>

        <div class="bar-label">{{ b.label }}</div>


          </div>
        </div>
      </div>
  </div>
</template>

<script>
import { PROJECT_DATA_URL } from "@/assets.js";

export default {
  name: "ProjectsAddedChart",



  data() {
    return {
      loading: true,
      bars: [],
      totalAllYears: 0,
      yearRangeLabel: "",
      barCanvasPx:340,

    };
  },

  mounted() {
    this.fetchAndBuild();
  },

  methods: {
    async fetchAndBuild() {
      try {
        const resp = await fetch(PROJECT_DATA_URL);
        const data = await resp.json();

        // Use entries so we can keep the project key for fallback naming
        const projects = Object.entries(data || {}).map(([key, p]) => ({
          ...p,
          __key: key
        }));

        const liveProjects = projects.filter((p) => p && p.status === "live");

        const series = this.buildCumulativeYearSeries(liveProjects);
        this.yearRangeLabel = series.rangeLabel;

        this.totalAllYears = series.values.length ? series.values[series.values.length - 1] : 0;

        const maxVal = Math.max(...series.values.map(v => Number(v) || 0), 1);

        this.bars = series.labels.map((label, i) => {
          const v = Number(series.values[i]) || 0;

          return {
            label,
            value: v,
            heightPx: Math.round((v / maxVal) * this.barCanvasPx),
          };
        });
      } catch (e) {
        console.error("ProjectsAddedChart failed:", e);
        this.bars = [];
        this.yearRangeLabel = "";
      } finally {
        this.loading = false;
      }
    },

    buildCumulativeYearSeries(projects) {
      const perYear = this.buildPerYearSeries(projects);

      const cumulativeValues = [];
      let running = 0;

      for (const v of perYear.values) {
        running += Number(v) || 0;
        cumulativeValues.push(running);
      }

      const cumulativeProjectsByYear = [];
      let runningNames = [];

      for (const names of perYear.projectsByYear) {
        runningNames = runningNames.concat(Array.isArray(names) ? names : []);
        const uniq = Array.from(new Set(runningNames));
        cumulativeProjectsByYear.push(
          uniq.slice().sort((a, b) => String(a).localeCompare(String(b)))
        );
        runningNames = uniq;
      }

      return {
        labels: perYear.labels,
        values: cumulativeValues,
        projectsByYear: cumulativeProjectsByYear,
        rangeLabel: perYear.rangeLabel
      };
    },


    buildPerYearSeries(projects) {
      const byYear = {};
      const START_YEAR = 2013;

      for (const p of projects) {
        const d = this.getProjectDate(p);
        if (!d) continue;
        
        const yr = d.getFullYear();
        const y = String(yr < START_YEAR ? START_YEAR : yr);
        if (!byYear[y]) byYear[y] = [];
        byYear[y].push(this.getProjectName(p));
      }        
      const allYears = Object.keys(byYear).sort();
      if (allYears.length === 0) {
        return { labels: [], values: [], projectsByYear: [], rangeLabel: "" };
      }

      const maxYear = Math.max(...allYears.map((y) => Number(y)));

      const years = [];
      for (let y = START_YEAR; y <= maxYear; y++) {
        years.push(String(y));
      }

      const labels = years;
      const values = years.map((y) => (byYear[y] ? byYear[y].length : 0));
      
      const projectsByYear = years.map((y) =>
        byYear[y]
          ? byYear[y].slice().sort((a, b) => String(a).localeCompare(String(b)))
          : []
      );

      const rangeLabel =
        years.length === 1 ? years[0] : `${years[0]}–${years[years.length - 1]}`;

      return { labels, values, projectsByYear, rangeLabel };

    },

    // Tries to extract a readable name for a project.
    getProjectName(p) {
      const name =
        (p && (p.name || p.title || p.project_name || p.projectName || p.short_name || p.shortName)) ||
        null;

      if (name) return String(name);

      // fallback to the key from your JSON
      if (p && p.__key) return String(p.__key);

      return "Unnamed project";
    },

    // Update keys if dataset uses different date field.
    getProjectDate(p) {
      const raw =
        (p &&
          (p.created_at ||
            p.createdAt ||
            p.date ||
            p.added_at ||
            p.addedAt ||
            p.timestamp ||
            p.created)) ||
        null;

      if (!raw) return null;

      const d = new Date(raw);
      return isNaN(d.getTime()) ? null : d;
    },
  }
};
</script>

<style scoped>


.chart-card {
  width: 100%;
  border-radius: 12px;
  padding: 12px 14px;
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.chart-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}

.chart-title {
  font-weight: 800;
  color: rgba(0, 0, 0, 0.75);
}

.chart-subtitle {
  font-size: 0.95rem;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
}

.chart-body {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  padding-top: 10px;
  position: relative;
}





.chart-empty {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.05);
  color: rgba(0, 0, 0, 0.6);
  font-size: 0.95rem;
  padding: 10px;
}

.bars {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  position: relative;
  background: #f7f7f5;
  padding: 12px 12px 8px 12px;
  border-radius: 12px;
  border: 1px solid rgba(0,0,0,0.05);
}


.bar-col {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}

.bar-value-top {
  font-size: 0.8rem;
  font-weight: 800;
  color: rgba(0, 0, 0, 0.7);
  height: auto;          
  margin-bottom: 2px; 
}

.bar-slot {
  height: 380px;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
}


.bar {
  width: 85%;
  border-radius: 10px;
  background: rgba(242, 169, 0, 0.85);
  border: 1px solid rgba(0, 0, 0, 0.08);
  transition: height 300ms ease;
}

.bar-col:hover .bar {
  background: rgba(242, 169, 0, 1);
}

.bar-label {
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.55);
  white-space: nowrap;
}

.x-note {
  margin-top: 8px;
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.55);
}
</style>

